import { Badge, Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { Link } from '@reach/router';
import moment from 'moment';

import { statusColorScheme } from '../../AccessPoints/List/Card';
import { HubCounterListItem } from './query';

const normalizeEmail = (value?: string | null) => value?.trim().toLowerCase() || '';

/** Login claim is frozen once the counter is queued to Amazon. Earlier statuses still follow the user email. */
const AMAZON_QUEUED_STATUSES = new Set(['CONFIRMED', 'ACTIVE', 'INACTIVE', 'RETRY']);

export const loginClaimDiffersFromUserEmail = ({ loginClaim, userEmail }: { loginClaim?: string | null; userEmail?: string | null }) => {
  const claim = normalizeEmail(loginClaim);
  const email = normalizeEmail(userEmail);
  return Boolean(claim && email && claim !== email);
};

export const emailCorrectedColorScheme = (createFeedSubmitted?: boolean | null) => (createFeedSubmitted ? 'red' : 'green');

export const showsQueuedEmailMismatch = ({
  status,
  loginClaim,
  userEmail,
}: {
  status?: string | null;
  loginClaim?: string | null;
  userEmail?: string | null;
}) => Boolean(status && AMAZON_QUEUED_STATUSES.has(status) && loginClaimDiffersFromUserEmail({ loginClaim, userEmail }));

export const buildHubCounterStatusBadges = ({
  displayState,
  hubCounter,
  emailChangedAfterWaitlist,
  createFeedSubmitted,
}: HubCounterListItem) => {
  const badges = [{ label: `Amazon: ${hubCounter.status}`, colorScheme: statusColorScheme[hubCounter.status] ?? 'gray' }];

  if (displayState === 'LOGIN_REQUIRED') {
    badges.push({ label: 'Login erforderlich', colorScheme: 'yellow' });
  }

  if (
    showsQueuedEmailMismatch({
      status: hubCounter.status,
      loginClaim: hubCounter.loginClaim,
      userEmail: hubCounter.dropPoint?.user?.email,
    })
  ) {
    badges.push({ label: 'Emails unterschiedlich', colorScheme: 'red' });
  }

  if (emailChangedAfterWaitlist) {
    badges.push({ label: 'Amazon: Mail korrigiert', colorScheme: emailCorrectedColorScheme(createFeedSubmitted) });
  }

  if (hubCounter.hubCounterID) {
    badges.push({
      label: hubCounter.isActive ? 'Amazon aktiv' : 'Amazon inaktiv',
      colorScheme: hubCounter.isActive ? 'green' : 'red',
    });
  }

  badges.push({
    label: hubCounter.dropPoint?.accessPoint?.status ? `UPS: ${hubCounter.dropPoint.accessPoint.status}` : 'UPS: Nicht angelegt',
    colorScheme: hubCounter.dropPoint?.accessPoint?.status
      ? (statusColorScheme[hubCounter.dropPoint.accessPoint.status] ?? 'gray')
      : 'gray',
  });

  badges.push({
    label: hubCounter.dropPoint.isActive ? 'DropPoint aktiv' : 'DropPoint inaktiv',
    colorScheme: hubCounter.dropPoint.isActive ? 'green' : 'red',
  });

  return badges;
};

export const HubCounterCard = (props: HubCounterListItem) => {
  const { hubCounter } = props;
  const { dropPoint } = hubCounter;
  const { colorMode } = useColorMode();
  const statusBadges = buildHubCounterStatusBadges(props);

  return (
    <Box
      as={Link}
      to={`/users/${dropPoint.user.id}`}
      width="100%"
      rounded="md"
      borderWidth="1px"
      py="4"
      px="6"
      _hover={{ boxShadow: 'md', backgroundColor: colorMode === 'light' ? 'white' : 'gray.700' }}
    >
      <Flex flexWrap="wrap" mb="2" gap="2">
        {statusBadges.map((badge) => (
          <Badge key={badge.label} colorScheme={badge.colorScheme}>
            {badge.label}
          </Badge>
        ))}
      </Flex>
      <Text mb="1" fontFamily="heading" fontSize="lg" fontWeight="bold">
        {dropPoint.locationName || `${dropPoint.user.firstName} ${dropPoint.user.lastName}`}
      </Text>
      {dropPoint.address && (
        <Text mb="2">
          {dropPoint.address.street} {dropPoint.address.houseNumber}
          <br />
          {dropPoint.address.postalCode} {dropPoint.address.city}
        </Text>
      )}
      {hubCounter.hubCounterID ? (
        <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
          Hub Counter ID: {hubCounter.hubCounterID}
        </Text>
      ) : null}
      <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
        Registrierungsdatum: {moment(dropPoint.createdAt).format('DD.MM.YYYY HH:mm')}
      </Text>
      {(hubCounter.status === 'REJECTED' || hubCounter.status === 'RETRY') && hubCounter.rejectionTimestamp ? (
        <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
          Abgelehnt am: {moment(hubCounter.rejectionTimestamp).format('DD.MM.YYYY HH:mm')}
        </Text>
      ) : null}
      {hubCounter.deactivatedUntil ? (
        <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'} fontSize="sm">
          Deaktiviert bis: {moment(hubCounter.deactivatedUntil).format('DD.MM.YYYY HH:mm')}
        </Text>
      ) : null}
    </Box>
  );
};
